import { toast } from 'react-toastify';
import { BarChart3, ListChecks, PackagePlus, Send, Sparkles, Wand2, X } from 'lucide-react';

import {
    useUpdateManualSupplyOrderProductStockVariationsMutation,
    useUpdateManualSupplyOrderForecastsMutation,
    GetManualSupplyOrdersDocument,
    GetManualSupplyOrderSupplierProductsStockVariationDocument,
    useMarkSupplyOrdersAsReadyMutation,
    TotadminPermission,
} from 'data/__generated__';

import { ActionBar } from 'components/ActionBar';
import { useCallback, useEffect } from 'react';
import { handleUpdateResults } from 'handlers';

export const ManualSupplyOrdersActionBar = ({
    selectedSupplyOrderIds,
    setSelectedSupplyOrderIds,
    manualSupplyOrders,
    handleUpdateQuantitiesFromPredictions,
    setUpdateStockVariationsLoading,
    setUpdateForecastsLoading,
    onAddProductsToSupplier,
}: {
    selectedSupplyOrderIds: string[];
    setSelectedSupplyOrderIds: (ids: string[]) => void;
    manualSupplyOrders: Array<{ _id: string }>;
    handleUpdateQuantitiesFromPredictions: () => void;
    setUpdateStockVariationsLoading: (loading: boolean) => void;
    setUpdateForecastsLoading: (loading: boolean) => void;
    onAddProductsToSupplier: () => void;
}) => {
    const [updateManualSupplyOrderProductStockVariations, { loading: updateStockVariationsLoading }] =
        useUpdateManualSupplyOrderProductStockVariationsMutation({
            refetchQueries: [GetManualSupplyOrderSupplierProductsStockVariationDocument],
        });
    const [updateManualSupplyOrderForecasts, { loading: updateForecastsLoading }] =
        useUpdateManualSupplyOrderForecastsMutation({
            refetchQueries: [GetManualSupplyOrdersDocument],
        });

    const [markSupplyOrdersAsReady, { loading: markSupplyOrdersAsReadyLoading }] = useMarkSupplyOrdersAsReadyMutation({
        refetchQueries: [GetManualSupplyOrdersDocument],
    });

    useEffect(() => {
        setUpdateStockVariationsLoading(updateStockVariationsLoading);
    }, [updateStockVariationsLoading]);

    useEffect(() => {
        setUpdateForecastsLoading(updateForecastsLoading);
    }, [updateForecastsLoading]);

    const handleUpdateStockVariations = useCallback(async () => {
        if (selectedSupplyOrderIds.length === 0) {
            toast.warning('Veuillez sélectionner au moins une commande');
            return;
        }

        const { data, errors } = await updateManualSupplyOrderProductStockVariations({
            variables: {
                supplyOrderIds: selectedSupplyOrderIds,
            },
        });

        handleUpdateResults({
            data: data?.updateManualSupplyOrderProductStockVariations,
            errors,
            errorMessage: 'Erreur lors de la mise à jour des variations de stock',
            successMessage: 'Les variations de stock ont été mises à jour avec succès !',
        });
    }, [selectedSupplyOrderIds, updateManualSupplyOrderProductStockVariations]);

    const handleUpdateForecasts = useCallback(async () => {
        if (selectedSupplyOrderIds.length === 0) {
            toast.warning('Veuillez sélectionner au moins une commande');
            return;
        }

        const { data, errors } = await updateManualSupplyOrderForecasts({
            variables: {
                supplyOrderIds: selectedSupplyOrderIds,
            },
        });

        handleUpdateResults({
            data: data?.updateManualSupplyOrderForecasts,
            errors,
            errorMessage: 'Erreur lors de la mise à jour des forecasts',
            successMessage: 'Les forecasts ont été mis à jour avec succès !',
        });
    }, [selectedSupplyOrderIds, updateManualSupplyOrderForecasts]);

    const handleMarkSupplyOrdersAsReady = useCallback(async () => {
        if (selectedSupplyOrderIds.length === 0) {
            toast.warning('Veuillez sélectionner au moins une commande');
            return;
        }

        const { data, errors } = await markSupplyOrdersAsReady({
            variables: { supplyOrderIds: selectedSupplyOrderIds },
        });
        handleUpdateResults({
            data: data?.markSupplyOrdersAsReady,
            errors,
            errorMessage: "Une erreur est survenue lors du passage des achats en 'à envoyer'",
            successMessage: 'Achat(s) mis en "À envoyer"',
        });
    }, [selectedSupplyOrderIds, markSupplyOrdersAsReady]);

    const handleSelectAllSupplyOrders = useCallback(() => {
        const allSupplyOrderIds = manualSupplyOrders.map((supplyOrder) => supplyOrder._id);
        setSelectedSupplyOrderIds(allSupplyOrderIds);
    }, [manualSupplyOrders, setSelectedSupplyOrderIds]);

    const handleDeselectAllSupplyOrders = useCallback(() => {
        setSelectedSupplyOrderIds([]);
    }, [setSelectedSupplyOrderIds]);

    const actionSections = [
        {
            title: 'Sélection',
            actions: [
                {
                    icon: <ListChecks />,
                    label: 'Tout sélectionner',
                    onClick: handleSelectAllSupplyOrders,
                    hidden: selectedSupplyOrderIds.length === manualSupplyOrders.length,
                },
                {
                    icon: <X />,
                    label: 'Tout désélectionner',
                    onClick: handleDeselectAllSupplyOrders,
                    hidden: selectedSupplyOrderIds.length === 0,
                },
            ],
        },
        {
            title: 'Actions sur les commandes',
            actions: [
                {
                    icon: <Send />,
                    label: 'Marquer comme "À envoyer"',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    onClick: handleMarkSupplyOrdersAsReady,
                    hidden: selectedSupplyOrderIds.length === 0,
                    loading: markSupplyOrdersAsReadyLoading,
                },
                {
                    icon: <Wand2 />,
                    label: 'Utiliser les prédictions',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    onClick: handleUpdateQuantitiesFromPredictions,
                    hidden: selectedSupplyOrderIds.length === 0,
                },
                {
                    icon: <BarChart3 />,
                    label: 'Mettre à jour les variations de stock',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    onClick: handleUpdateStockVariations,
                    loading: updateStockVariationsLoading,
                    hidden: selectedSupplyOrderIds.length === 0,
                },
                {
                    icon: <Sparkles />,
                    label: 'Mettre à jour les prédictions',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    onClick: handleUpdateForecasts,
                    loading: updateForecastsLoading,
                    hidden: selectedSupplyOrderIds.length === 0,
                },
                {
                    icon: <PackagePlus />,
                    label: 'Ajouter un produit au fournisseur',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    onClick: onAddProductsToSupplier,
                    hidden: selectedSupplyOrderIds.length !== 1,
                },
            ],
        },
    ];

    return <ActionBar actionSections={actionSections} isInitiallyOpen={selectedSupplyOrderIds.length > 0} />;
};
